import { useQueryClient } from "@tanstack/react-query";
import { Plus, Search } from "lucide-react";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import {
  useCreateEncounter,
  useSearchPatients,
  useSyncPatientByEhr,
} from "@elation/api-client/src/stitch-web";
import type { PatientSearchItem } from "@elation/api-client/src/stitch-web/models";

import { PatientEntity } from "@src/features/patients";
import { ErrorMessage } from "@src/shared/components/general/ErrorMessage";
import {
  HorizontalFlexbox,
  VerticalFlexbox,
} from "@src/shared/components/layout/Flexbox";
import { Avatar, AvatarFallback } from "@src/shared/components/ui/avatar";
import { Button } from "@src/shared/components/ui/button";
import { Label } from "@src/shared/components/ui/label";
import { Skeleton } from "@src/shared/components/ui/skeleton";
import {
  Combobox,
  ComboboxAnchor,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  type ComboboxInputValueChangeMeta,
  ComboboxItem,
  ComboboxList,
} from "@src/shared/components/v2/Combobox";
import { cn } from "@src/shared/lib/utils";
import { navigationV2 } from "@src/shared/navigation/v2";
import {
  getHttpStatus,
  getServerErrorMessage,
} from "@src/shared/utils/http/errors";
import { captureException } from "@src/shared/utils/observability/sentry";

export interface PatientSelectorPatientSummaryV2 {
  name: string;
  initials: string;
  metadata?: string | null;
}

export interface PatientSelectorV2Props {
  patient?: PatientSelectorPatientSummaryV2 | null;
  open?: boolean;
  canChangePatient?: boolean;
  onOpenChange: (open: boolean) => void;
  onPatientSelect?: (patient: PatientSearchItem) => void;
}

interface PatientOption {
  key: string;
  patient: PatientSearchItem;
  name: string;
  initials: string;
  metadata: string | null;
  helperText?: string | null;
}

function toPatientOption(patient: PatientSearchItem): PatientOption {
  return {
    key:
      PatientEntity.searchResultKey(patient) ??
      `patient-search:${PatientEntity.displayName(patient) ?? patient.externalPatientId ?? "unknown"}`,
    patient,
    name: PatientEntity.displayName(patient) ?? "Unknown patient",
    initials: PatientEntity.initials(patient) ?? "?",
    metadata: PatientEntity.listMetadata(patient) ?? "",
    helperText:
      patient.patientId == null
        ? `Will sync from ${PatientEntity.ehrLabel(patient) ?? "EHR"}`
        : null,
  };
}

function PatientAvatar({
  patient,
}: {
  patient?: PatientSelectorPatientSummaryV2 | null;
}) {
  return (
    <Avatar className="size-8 border border-border bg-background">
      <AvatarFallback className="bg-primary/10 font-mono text-[10px] font-semibold text-primary">
        {patient?.initials || <Plus className="size-4" aria-hidden="true" />}
      </AvatarFallback>
    </Avatar>
  );
}

function PatientOptionRow({
  option,
  isDisabled,
  isSelecting,
  onSelect,
}: {
  option: PatientOption;
  isDisabled: boolean;
  isSelecting: boolean;
  onSelect: (patient: PatientSearchItem) => void;
}) {
  return (
    <ComboboxItem<PatientOption>
      className={cn(
        "flex w-full items-center gap-2.5 rounded-none border-b border-border px-3 py-2.5 text-left transition-colors last:border-b-0 hover:bg-muted/50",
        isDisabled && "cursor-wait opacity-70 hover:bg-transparent",
      )}
      disabled={isDisabled}
      hideIndicator
      aria-disabled={isDisabled}
      onSelect={(selectedOption) => onSelect(selectedOption.patient)}
      value={option}
    >
      <PatientAvatar patient={option} />
      <div className="min-w-0 flex-1">
        <p className="truncate text-[13px] font-semibold text-foreground">
          {option.name}
        </p>
        <p className="mt-0.5 truncate font-mono text-[10px] text-muted-foreground">
          {option.metadata || option.helperText || "Patient"}
        </p>
      </div>
      {isSelecting ? (
        <span className="font-mono text-[10px] text-primary">creating...</span>
      ) : null}
    </ComboboxItem>
  );
}

export function PatientSelectorV2({
  patient,
  open = false,
  canChangePatient = true,
  onOpenChange,
  onPatientSelect,
}: PatientSelectorV2Props) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [query, setQuery] = useState("");
  const [openingPatientKey, setOpeningPatientKey] = useState<string | null>(
    null,
  );
  const [createEncounterError, setCreateEncounterError] = useState<
    string | null
  >(null);
  const syncPatientByEhr = useSyncPatientByEhr();
  const createEncounter = useCreateEncounter();
  const isSelectingPatient =
    createEncounter.isPending || syncPatientByEhr.isPending;
  const canOpenSelector = canChangePatient && !isSelectingPatient;
  const showSearchInput = canChangePatient && (open || !patient);
  const patientSearch = useSearchPatients(
    {
      q: query.trim() || undefined,
      sortBy: "lastName",
      order: "asc",
      limit: 6,
    },
    {
      query: {
        enabled: canChangePatient,
        retry: (failureCount, err) => {
          const status = getHttpStatus(err);
          if (status != null && status >= 400 && status < 500) return false;
          return failureCount < 2;
        },
      },
    },
  );
  const patientOptions = useMemo(
    () => (patientSearch.data?.data.items ?? []).map(toPatientOption),
    [patientSearch.data],
  );
  const visibleError =
    createEncounterError ??
    (patientSearch.isError ? patientSearch.error : null);
  const isSearching = patientSearch.isLoading || patientSearch.isFetching;

  useEffect(() => {
    if (!open) return;
    window.requestAnimationFrame(() => inputRef.current?.focus());
  }, [open]);

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen && !canOpenSelector) return;
    if (nextOpen) setCreateEncounterError(null);
    onOpenChange(nextOpen);
  };
  const handleInputValueChange = (
    value: string,
    meta: ComboboxInputValueChangeMeta,
  ) => {
    if (meta.source === "input" || meta.source === "clear") {
      setQuery(value);
    }
  };
  const handlePatientSelect = async (selectedPatient: PatientSearchItem) => {
    if (onPatientSelect) {
      onPatientSelect(selectedPatient);
      return;
    }

    if (!canChangePatient || isSelectingPatient) return;

    const key = PatientEntity.searchResultKey(selectedPatient);
    setOpeningPatientKey(key);
    setCreateEncounterError(null);

    try {
      let patientId = selectedPatient.patientId;

      if (patientId == null) {
        if (!selectedPatient.ehr || selectedPatient.externalPatientId == null) {
          throw new Error("This patient cannot be synced yet.");
        }

        const syncResult = await syncPatientByEhr.mutateAsync({
          ehr: selectedPatient.ehr,
          patientId: String(selectedPatient.externalPatientId),
        });
        patientId = syncResult.data.patient.id;
      }

      const result = await createEncounter.mutateAsync({
        data: { patientId },
      });

      await queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === "/gw/stitch-web/api/encounters",
      });

      onOpenChange(false);
      navigate(navigationV2.visit(result.data.id), {
        replace: true,
        state: { source: "v2 visit create" },
      });
    } catch (error) {
      captureException(error, {
        tags: { feature: "encounters-v2", action: "create-visit" },
        extra: {
          patientId: selectedPatient.patientId,
          ehr: selectedPatient.ehr,
          externalPatientId: selectedPatient.externalPatientId,
        },
      });
      setCreateEncounterError(
        getServerErrorMessage(error) ??
          "Unable to create the visit. Please try again.",
      );
      toast.error("Unable to create visit. Please try again.");
    } finally {
      setOpeningPatientKey(null);
    }
  };

  return (
    <Combobox<PatientOption>
      closeOnSelect={false}
      itemToStringValue={(option) => `${option.name} ${option.metadata ?? ""}`}
      itemToValue={(option) => option.key}
      items={patientOptions}
      label="Search or add a patient"
      open={canChangePatient ? open : false}
      resetInputOnClose={false}
      shouldFilter={false}
      onOpenChange={handleOpenChange}
    >
      <ComboboxAnchor asChild>
        <div
          className="flex min-w-0 flex-1 items-center gap-2"
          data-combobox-anchor=""
        >
          <PatientAvatar patient={patient} />
          {showSearchInput ? (
            <div className="relative w-full max-w-[280px]">
              <Label htmlFor={inputId} className="sr-only">
                Search or add a patient
              </Label>
              <Search
                className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground"
                aria-hidden="true"
              />
              <ComboboxInput
                ref={inputRef}
                asAnchor={false}
                id={inputId}
                placeholder="Search or add a patient"
                autoComplete="off"
                className="h-9 rounded-[9px] bg-background pr-8 pl-9 text-[13px]"
                disabled={isSelectingPatient}
                onFocus={() => handleOpenChange(true)}
                onValueChange={handleInputValueChange}
                showClear
              />
            </div>
          ) : patient ? (
            <HorizontalFlexbox
              alignItems="center"
              className="min-w-0 flex-1 gap-2"
            >
              <div className="min-w-0 leading-tight">
                <p className="truncate text-[13.5px] font-semibold text-foreground">
                  {patient.name}
                </p>
                <p className="mt-0.5 truncate font-mono text-[10.5px] text-muted-foreground">
                  {patient.metadata || "Patient details added"}
                </p>
              </div>
              {canChangePatient ? (
                <Button
                  type="button"
                  variant="link"
                  className="h-auto p-0 font-mono text-[10.5px] text-primary hover:text-primary/80 disabled:opacity-45"
                  disabled={!canOpenSelector}
                  onClick={() => handleOpenChange(true)}
                >
                  change
                </Button>
              ) : null}
            </HorizontalFlexbox>
          ) : (
            <div className="min-w-0 leading-tight">
              <p className="truncate text-[13.5px] font-semibold text-foreground">
                New visit
              </p>
              <p className="mt-0.5 truncate font-mono text-[10.5px] text-muted-foreground">
                No patient selected
              </p>
            </div>
          )}
        </div>
      </ComboboxAnchor>

      <ComboboxContent
        align="start"
        portalled={false}
        sideOffset={4}
        className="w-[280px] overflow-hidden rounded-[9px] p-0 shadow-[0_18px_45px_rgba(15,23,42,0.14)]"
      >
        {visibleError ? (
          <ErrorMessage
            className="block p-3"
            error={visibleError}
            fallback="Unable to search patients."
          />
        ) : isSearching ? (
          <VerticalFlexbox className="gap-2 p-3">
            {[0, 1, 2].map((index) => (
              <HorizontalFlexbox key={index} className="gap-2.5">
                <Skeleton className="size-8 rounded-full" />
                <VerticalFlexbox className="flex-1 gap-1">
                  <Skeleton className="h-3 w-28" />
                  <Skeleton className="h-2.5 w-40" />
                </VerticalFlexbox>
              </HorizontalFlexbox>
            ))}
          </VerticalFlexbox>
        ) : patientOptions.length > 0 ? (
          <ComboboxList<PatientOption>
            aria-label="Patient search results"
            items={patientOptions}
          >
            {(option) => (
              <PatientOptionRow
                key={option.key}
                option={option}
                isDisabled={isSelectingPatient}
                isSelecting={
                  isSelectingPatient && openingPatientKey === option.key
                }
                onSelect={(patient) => void handlePatientSelect(patient)}
              />
            )}
          </ComboboxList>
        ) : (
          <ComboboxEmpty className="px-4 py-6 text-center text-sm text-muted-foreground">
            No patients found.
          </ComboboxEmpty>
        )}
      </ComboboxContent>
    </Combobox>
  );
}
